import { memo, useCallback, useRef, useState } from 'react'
import { AnimatedSpinner } from '../../components/AnimatedSpinner'
import { Member } from './components/Member'
import { MemberInfo } from './components/Member/MemberInfo'
import { OnboardingBtn } from './components/OnboardingBtn'
import { OnboardingForm } from './components/OnboardingForm'
import { team } from './constants'

const NapsterLogo = '/images/logo_napster_white.svg'

const MEMBER_FADE_OUT_DURATION = 1000

const OnboardingPage: React.FC = memo(() => {
  const [scene, setScene] = useState('')
  const [isHoverDisabled, setDisableHover] = useState(true)
  const [showMembers, setShowMembers] = useState(true)
  const [showLogo, setShowLogo] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [showMemberInfo, setShowMemberInfo] = useState(false)
  const [memberInfoFadingOut, setMemberInfoFadingOut] = useState(false)
  const [showNextButton, setShowNextButton] = useState(false)
  const loadedVideosCount = useRef(0)

  const handleVideoLoaded = useCallback(() => {
    loadedVideosCount.current += 1
    if (loadedVideosCount.current === team.length) {
      setIsLoading(false)
    }
  }, [])

  const handleInteraction = () => {
    setScene('start')
    // Trigger member info fade-in with a slight delay
    setTimeout(() => {
      setShowMemberInfo(true)
    }, 300)
  }

  const enableHover = () => {
    setDisableHover(false)
    // Start fade-out animation for member info
    setMemberInfoFadingOut(true)

    // Hide member info after fade-out animation completes
    setTimeout(() => {
      setShowMemberInfo(false)
      setMemberInfoFadingOut(false)
    }, 600) // Match fade-out animation duration

    // Show Next button with fade-in after a slight delay
    setTimeout(() => {
      setShowNextButton(true)
    }, 300)
  }

  const handleStartClick = () => {
    setScene('form')
    setShowMemberInfo(false)
    setMemberInfoFadingOut(false)
    setShowNextButton(false)

    setTimeout(() => {
      setShowMembers(false)
    }, MEMBER_FADE_OUT_DURATION)
  }

  return (
    <div className="h-screen w-screen  overflow-hidden font-dmsans text-white flex flex-col justify-center items-center bg-[url('/images/twin-bg.jpeg')] bg-cover bg-center">
      {isLoading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-md bg-black/50">
          <AnimatedSpinner />
        </div>
      )}
      {showLogo && (
        <img
          src={NapsterLogo}
          alt="Napster Logo"
          className="fixed top-8 left-8 !h-[22px] md:!h-[30px] z-[101]"
        />
      )}
      <div className="fixed left-0 right-0 bottom-0 top-[5%] overflow-hidden flex  justify-center [perspective:1000px] [transform-style:preserve-3d]">
        {showMembers &&
          team.map(member => (
            <Member
              {...member}
              key={member.name}
              scene={scene}
              isHoverDisabled={isHoverDisabled}
              enableHover={enableHover}
              onVideoLoaded={handleVideoLoaded}
            />
          ))}

        {scene === 'start' && showMemberInfo && (
          <div className="absolute bottom-20 left-0 w-full flex justify-center items-center z-[101]">
            {team.map((member, index) => (
              <div
                key={member.name}
                className="w-[11%] lg:-mr-[1%] scale-[40%] lg:scale-[60%]"
                style={{
                  animationDelay: memberInfoFadingOut
                    ? `${(team.length - 1 - index) * 50}ms`
                    : `${index * 100}ms`,
                }}
              >
                <MemberInfo
                  name={member.name}
                  role={member.role}
                  className={
                    memberInfoFadingOut ? 'fade-out-down' : 'fade-in-up-delayed'
                  }
                />
              </div>
            ))}
          </div>
        )}

        <div className="fixed left-0 right-0 px-24 -bottom-1/4 top-3/4 flex flex-col items-center gap-4 pt-4 z-[100]">
          <div className="absolute inset-0 rounded-[50%] bg-[rgba(0,0,0,0.9)] blur-[40px]" />

          {scene === '' && (
            <OnboardingBtn onClick={handleInteraction} text="Start" />
          )}

          {scene === 'start' && !isHoverDisabled && showNextButton && (
            <div className="fade-in">
              <OnboardingBtn onClick={handleStartClick} text="Next" />
            </div>
          )}
        </div>
      </div>
      {scene && (
        <OnboardingForm
          scene={scene}
          showMembers={showMembers}
          setShowLogo={setShowLogo}
        />
      )}
    </div>
  )
})

export { OnboardingPage }
